import { Injectable, Inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject } from 'rxjs';
import {
  IExtendedRealmSettings,
  IRealmSetting,
} from '../../interfaces/IRealmSetting';
import { NbThemeService } from '@nebular/theme';
import { TextChangeService } from '../text-change.service';
import { Title } from '@angular/platform-browser';
import { DEPLOY_URL } from '../../../../../deploy-url';

@Injectable({
  providedIn: 'root',
})
export class RealmSettingService {
  realmConfigs: BehaviorSubject<IRealmSetting> =
    new BehaviorSubject<IRealmSetting>(null);
  extendedRealmConfigs: BehaviorSubject<IExtendedRealmSettings> =
    new BehaviorSubject<IExtendedRealmSettings>(null);

  constructor(
    private http: HttpClient,
    private readonly themeService: NbThemeService,
    private textChangeService: TextChangeService,
    private titleService: Title,
    @Inject(DEPLOY_URL) private deployUrl: string,
  ) {}

  setConfigs(): Promise<any> {
    return Promise.all([
      this.setRealmConfigs(),
      this.setExtendedRealmConfigs(),
    ]).then((result) => this.textChangeService.loadTexts());
  }

  setRealmConfigs() {
    return new Promise<IRealmSetting>((resolve, reject) => {
      this.http.get<any>('/api/config').subscribe(
        (next) => {
          this.realmConfigs.next(next);
          resolve();
        },
        (error) => {
          resolve();
        }
      );
    });
  }

  getRealmConfigs() {
    return this.realmConfigs.asObservable();
  }

  setExtendedRealmConfigs() {
    return new Promise<IRealmSetting>((resolve, reject) => {
      this.http.get<any>('/api/config/extra').subscribe(
        (extra) => {
          this.extendedRealmConfigs.next(extra);

          if (extra && extra.businessName) {
            this.titleService.setTitle(extra.businessName);
          }

          if (extra && extra.theme) {
            let theme = extra.theme;
            if (localStorage.getItem('darkMode') === 'true') {
              theme = theme + '-dark';
            }
            this.themeService.changeTheme(theme);
            this.updateFavicon(theme);
            this.updateManifest(theme);
          }

          resolve();
        },
        (error) => {
          resolve();
        }
      );
    });
  }

  getExtendedRealmConfigs() {
    return this.extendedRealmConfigs.asObservable();
  }

  updateFavicon(theme?: string): void {
    theme = this.resolveTheme(theme);
    if (!theme) { return; }
    const link: HTMLLinkElement = document.querySelector("link[rel*='icon']") || document.createElement('link');
    link.rel = 'icon';
    link.href = `${this.deployUrl}assets/images/${theme}-theme/favicon.ico`;
    document.head.appendChild(link);
  }

  updateManifest(theme?: string): void {
    if (!theme) {
      const extra = this.extendedRealmConfigs.getValue();
      if (!extra || !extra.theme) { return; }
      theme = extra.theme;
    }
    const extra = this.extendedRealmConfigs.getValue();
    const sizes = [72, 96, 128, 144, 152, 192, 384, 512];
    const manifest = {
      name: (extra && extra.businessName) || 'Multiversity',
      short_name: (extra && extra.businessName) || 'Multiversity',
      theme_color: '#0f5292',
      background_color: '#ffffff',
      display: 'standalone',
      scope: './',
      start_url: './',
      icons: sizes.map(s => ({
        src: `${this.deployUrl}assets/images/${theme}-theme/icons/icon-${s}x${s}.png`,
        sizes: `${s}x${s}`,
        type: 'image/png',
        purpose: 'maskable any',
      })),
    };
    const blob = new Blob([JSON.stringify(manifest)], { type: 'application/json' });
    const link: HTMLLinkElement = document.querySelector('link[rel="manifest"]');
    if (link) { link.href = URL.createObjectURL(blob); }
  }

  private resolveTheme(theme?: string): string {
    if (!theme) {
      const extra = this.extendedRealmConfigs.getValue();
      if (!extra || !extra.theme) { return null; }
      theme = extra.theme;
      if (localStorage.getItem('darkMode') === 'true') {
        theme = theme + '-dark';
      }
    }
    return theme;
  }
}
